import { defaultLocale, locales, type Locale } from "@shoppy/i18n";

export { defaultLocale, locales };
export type { Locale };

const STORAGE_KEY = "shoppy.admin.locale";

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (locales as readonly string[]).includes(value);
}

/**
 * `mn` is the default and the fallback, everywhere (docs/10 §5b). An operator's
 * explicit choice beats the browser's list, because in this office the browser
 * is frequently an English-language Windows install in front of a Mongolian
 * speaker.
 */
export function negotiateLocale(stored: string | null, acceptLanguage: string | null): Locale {
  if (isLocale(stored)) return stored;
  if (!acceptLanguage) return defaultLocale;

  const ranked = acceptLanguage
    .split(",")
    .map((part) => {
      const [tag = "", ...params] = part.trim().split(";");
      const q = params.find((p) => p.trim().startsWith("q="));
      const weight = q ? Number(q.trim().slice(2)) : 1;
      return { tag: tag.trim().toLowerCase(), weight: Number.isFinite(weight) ? weight : 0 };
    })
    .filter((entry) => entry.tag !== "" && entry.tag !== "*" && entry.weight > 0)
    .sort((a, b) => b.weight - a.weight);

  for (const { tag } of ranked) {
    // `mn-Cyrl-MN` and `en-US` both have to land; only the primary subtag matters.
    const primary = tag.split("-")[0];
    if (isLocale(primary)) return primary;
  }
  return defaultLocale;
}

export function readStoredLocale(): string | null {
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) ?? null;
  } catch {
    // Storage throws outright in a private window and under a policy that blocks
    // site data. A console that forgets the language is a worse morning, not a
    // broken screen.
    return null;
  }
}

export function storeLocale(locale: Locale): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, locale);
  } catch {
    /* see readStoredLocale */
  }
}

export function initialLocale(): Locale {
  return negotiateLocale(readStoredLocale(), globalThis.navigator?.languages?.join(",") ?? null);
}
